import { groupEntityName } from '../../../../backend/features/groups/shared';
import { getGroupFilter } from '../../../../backend/features/permissions/shared';
import { useUserState } from '../../../../ui/hooks';
import { mapDynamicFilters } from '@juvare/platform-sdk-react/ui/features/dynamic-columns';
import { useStorageList } from '@juvare/platform-sdk-react/ui/hooks';
import { isValid, parseISO } from 'date-fns';
/**
 * Converts from frontend account filter to backend storage client filter. See `mapAccountFilterReverse`.
 */
export function useAccountFilter({ advancedSearchItems, filterData, dynamicColumnData }) {
    var _a;
    const userState = useUserState();
    const groups = useStorageList(groupEntityName, (filterData === null || filterData === void 0 ? void 0 : filterData.groupMembership) && (filterData === null || filterData === void 0 ? void 0 : filterData.groupMembership.length) > 0
        ? { filter: [...getGroupFilter(userState), { op: 'in', id: filterData.groupMembership }] }
        : undefined, undefined, undefined, {
        revalidateOnFocus: false,
    });
    if (groups.isLoading) {
        return {
            isLoading: true,
        };
    }
    else if (groups.error) {
        return {
            isLoading: false,
            error: groups.error,
        };
    }
    else {
        return { data: mapAccountFilterInternal(advancedSearchItems, filterData, dynamicColumnData, (_a = groups.data) === null || _a === void 0 ? void 0 : _a.rows), isLoading: false };
    }
}
function getAdvancedFilters(advanced, advancedSearchItems) {
    const filters = [];
    try {
        const groupedByField = advanced.reduce(function (result, current) {
            result[current.field] = result[current.field] || [];
            result[current.field].push(current);
            return result;
        }, Object.create(null));
        Object.entries(groupedByField).forEach(([field, values]) => {
            const item = advancedSearchItems.find((item) => item.value === field);
            if (item && values.length) {
                if (values.length > 1) {
                    filters.push({ op: 'or', filter: values.map((value) => item.onGetStorageFilter(value.operator, value.value)) });
                }
                else if ((values === null || values === void 0 ? void 0 : values.length) == 1) {
                    filters.push(item.onGetStorageFilter(values[0].operator, values[0].value));
                }
            }
        });
    }
    catch (e) {
        console.error(e);
    }
    return filters;
}
/**
 * This function is exported so that unit test are easier to write. Do not depend on it.
 */
export function mapAccountFilterInternal(advancedSearchItems, filterData, dynamicColumnData, groups) {
    const { search, partialSearch, systemRole, firstName, lastName, email, phoneNumber, pagerNumber, orgAssociation, orgRoleSet, orgStatusSet, contactIssues, contactMethodSet, unaffiliatedOnly, systemStatus, loginBefore, loginAfter, updatedBefore, updatedAfter, registeredBefore, registeredAfter, orgRoleInStaticOrg, orgStatusInStaticOrg, advanced, } = checkAndRemoveEmptyArrays(filterData);
    const filter = [];
    if (advanced) {
        filter.push(...getAdvancedFilters(advanced, advancedSearchItems));
    }
    if (search) {
        filter.push({ column: 'search', op: 'full-text-query', value: search });
    }
    if (partialSearch) {
        filter.push({ column: 'partialSearch', op: 'contains', value: partialSearch });
    }
    if (systemRole) {
        filter.push({ column: 'systemRole', op: 'in', value: systemRole });
    }
    if (firstName) {
        filter.push({ column: 'firstName', op: '=', value: firstName });
    }
    if (lastName) {
        filter.push({ column: 'lastName', op: '=', value: lastName });
    }
    if (email) {
        filter.push({ column: 'email', op: '=', value: email });
    }
    if (phoneNumber) {
        filter.push({ column: 'contactPhoneNumbers', op: 'overlap', value: [phoneNumber] });
    }
    if (pagerNumber) {
        filter.push({ column: 'pagerNumbers', op: 'overlap', value: [pagerNumber] });
    }
    if (orgAssociation) {
        if (orgRoleSet || orgStatusSet) {
            if (orgRoleSet) {
                filter.push({
                    column: 'orgRole',
                    op: 'overlap',
                    value: orgAssociation.flatMap((orgId) => orgRoleSet.map((orgRole) => orgId + '_' + orgRole)),
                });
            }
            if (orgStatusSet) {
                filter.push({
                    column: 'orgStatus',
                    op: 'overlap',
                    value: orgAssociation.flatMap((orgId) => orgStatusSet.map((orgRole) => orgId + '_' + orgRole)),
                });
            }
        }
        else {
            filter.push({ column: 'orgAssociation', op: 'overlap', value: orgAssociation });
        }
    }
    else {
        if (orgRoleSet) {
            filter.push({
                column: 'orgRoleSet',
                op: 'overlap',
                value: orgRoleSet,
            });
        }
        if (orgStatusSet) {
            filter.push({
                column: 'orgStatusSet',
                op: 'overlap',
                value: orgStatusSet,
            });
        }
    }
    if (contactIssues === null || contactIssues === void 0 ? void 0 : contactIssues.includes('hasBouncedEmails')) {
        filter.push({ column: 'hasBouncedEmails', op: '=', value: true });
    }
    if (contactIssues === null || contactIssues === void 0 ? void 0 : contactIssues.includes('hasUnconfirmedPhoneNumbers')) {
        filter.push({ column: 'hasUnconfirmedPhoneNumbers', op: '=', value: true });
    }
    if (contactIssues === null || contactIssues === void 0 ? void 0 : contactIssues.includes('hasCanceledPhoneNumbers')) {
        filter.push({ column: 'hasCanceledPhoneNumbers', op: '=', value: true });
    }
    if (contactMethodSet) {
        filter.push({ column: 'contactMethodSet', op: 'overlap', value: contactMethodSet });
    }
    if (unaffiliatedOnly) {
        filter.push({ column: 'hasOrgMembership', op: '=', value: false });
        filter.push({ column: 'systemRole', op: 'not-in', value: ['system-admin', 'client-admin'] });
    }
    if (systemStatus) {
        filter.push({ column: 'systemStatus', op: 'in', value: systemStatus });
    }
    if (loginBefore != null) {
        // TODO need support from Platform for this kind of filter
        //filter.push({ column: 'lastLogin', op: '<', value: `${loginBefore}T00:00:00.000[${serverTimeZoneFromSettings}]`});
        const date = parseISO(loginBefore + 'T00:00:00.000');
        if (isValid(date)) {
            filter.push({ column: 'lastLogin', op: '<', value: date.getTime() });
        }
        else {
            console.warn('Invalid account filter login before date', loginBefore);
        }
    }
    if (loginAfter != null) {
        // TODO need support from Platform for this kind of filter
        //filter.push({ column: 'lastLogin', op: '>', value: `${loginAfter}T23:59:59.999[${serverTimeZoneFromSettings}]`});
        const date = parseISO(loginAfter + 'T23:59:59.999');
        if (isValid(date)) {
            filter.push({ column: 'lastLogin', op: '>', value: date.getTime() });
        }
        else {
            console.warn('Invalid account filter login after date', loginAfter);
        }
    }
    if (registeredBefore != null) {
        // TODO need support from Platform for this kind of filter
        //filter.push({ op: '<', registrationDate: `${registeredBefore}T00:00:00.000[${serverTimeZoneFromSettings}]`});
        const date = parseISO(registeredBefore + 'T00:00:00.000');
        if (isValid(date)) {
            filter.push({ op: '<', column: 'registrationDate', value: date.getTime() });
        }
        else {
            console.warn('Invalid account filter registered before date', loginBefore);
        }
    }
    if (registeredAfter != null) {
        // TODO need support from Platform for this kind of filter
        //filter.push({ op: '>', registrationDate: `${registeredAfter}T23:59:59.999[${serverTimeZoneFromSettings}]`});
        const date = parseISO(registeredAfter + 'T23:59:59.999');
        if (isValid(date)) {
            filter.push({ op: '>', column: 'registrationDate', value: date.getTime() });
        }
        else {
            console.warn('Invalid account filter registered after date', loginAfter);
        }
    }
    if (updatedBefore != null) {
        // TODO need support from Platform for this kind of filter
        //filter.push({ op: '<', updated: `${updatedBefore}T00:00:00.000[${serverTimeZoneFromSettings}]`});
        const date = parseISO(updatedBefore + 'T00:00:00.000');
        if (isValid(date)) {
            filter.push({ op: '<', updated: date.getTime() });
        }
        else {
            console.warn('Invalid account filter updated before date', loginBefore);
        }
    }
    if (updatedAfter != null) {
        // TODO need support from Platform for this kind of filter
        //filter.push({ op: '>', updated: `${updatedAfter}T23:59:59.999[${serverTimeZoneFromSettings}]`});
        const date = parseISO(updatedAfter + 'T23:59:59.999');
        if (isValid(date)) {
            filter.push({ op: '>', updated: date.getTime() });
        }
        else {
            console.warn('Invalid account filter updated after date', loginAfter);
        }
    }
    if (orgRoleInStaticOrg) {
        // Used only in organization members
        filter.push({
            column: 'orgRole',
            op: 'overlap',
            value: orgRoleInStaticOrg,
        });
    }
    if (orgStatusInStaticOrg) {
        // Used only in organization members
        filter.push({
            column: 'orgStatus',
            op: 'overlap',
            value: orgStatusInStaticOrg,
        });
    }
    if (groups) {
        for (const group of groups) {
            const groupFilter = JSON.parse(group.columns.criteriaString).filters;
            if (groupFilter.length === 1) {
                filter.push(Object.assign({ groupId: group.id }, groupFilter[0]));
            }
            else {
                filter.push({
                    groupId: group.id, // this id is needed only for mapAccountFilterReverse function to work
                    op: 'and',
                    filter: groupFilter, // empty groupFilter work properly here, platform server handles empty and filter by doing nothing (as it should)
                });
            }
        }
    }
    if (filterData) {
        filter.push(...mapDynamicFilters(filterData, dynamicColumnData));
    }
    if (filter.length) {
        filter[0] = Object.assign(Object.assign({}, filter[0]), { metadata: { ui: filterData } });
    }
    return filter;
}
function checkAndRemoveEmptyArrays(filterData) {
    return filterData
        ? Object.fromEntries(Object.entries(filterData).filter(([key, value]) => {
            if (Array.isArray(value) && value.length === 0) {
                console.warn('Account filter data has an empty array for ' + key + ' this should not happen');
                return false;
            }
            else {
                return true;
            }
        }))
        : {};
}
